/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useState} from 'react';
import {GlobalGroupProps} from '@ion/core/src/types/general';
import {FloatingIconButtonGroup} from '@ion/dls/src/components/buttons/FloatingIconButton';
import {IconSize} from '@ion/dls/src/types/types';
import {BasemapLayerTool} from '../types/types';
import MoreMenuButton from './MoreMenuButton';
import SimpleMapControlsBottomSheet from './SimpleMapControlsBottomSheet';

// Defines the props for the MoreMenuTool component.
export type MoreMenuToolProps = GlobalGroupProps & {
  tools?: BasemapLayerTool[];
  size?: IconSize;
  isSelected?: boolean;
  view: __esri.MapView,
  map: __esri.Map;
  basemapLayerRef: __esri.Layer;
  currentAccountId?: number;
  editGeofenceGraphic?: __esri.Graphic;
  setEditGeofenceGraphic?: Function;
  createOSMBasemap: Function;
  shouldShowTooltip?: boolean;
};

/**
 * The MoreMenuTool component provides the UI which displays the associated Icon
 * button within a SimpleMap that, when clicked, initiates the Bottom sheet
 *
 * ```typescript
 *
 *  <MoreMenuTool tools={tools}
 *    view={view}
 *    map={map}
 *    basemapLayerRef={basemapLayerRef}
 *    editGeofenceGraphic={editGeofenceGraphic} />
 * ```
 */
export const MoreMenuTool = ({
  tools,
  view,
  map,
  createOSMBasemap,
  accountMap,
  globalGroup,
  basemapLayerRef,
  currentAccountId,
  editGeofenceGraphic,
  setEditGeofenceGraphic,
  size = IconSize.large,
  isSelected = false,
  shouldShowTooltip = true
}: MoreMenuToolProps) => {
  const [isShowBottomSheet, setIsShowBottomSheet] = useState(false);
  const onShowBottomSheet = useCallback(() => {
    setIsShowBottomSheet(true);
  }, []);
  const onCloseBottomSheet = useCallback(() => {
    setIsShowBottomSheet(false);
  }, []);

  useEffect(() => {
    if (editGeofenceGraphic) {
      onShowBottomSheet();
    } else {
      onCloseBottomSheet();
    }
  }, [onShowBottomSheet, editGeofenceGraphic]);

  return (
    <>
      <FloatingIconButtonGroup>
        <MoreMenuButton size={size}
          isSelected={isSelected}
          shouldShowTooltip={shouldShowTooltip}
          onShowBottomSheet={onShowBottomSheet} />
      </FloatingIconButtonGroup>
      <SimpleMapControlsBottomSheet isShowBottomSheet={isShowBottomSheet}
        createOSMBasemap={createOSMBasemap}
        onCloseBottomSheet={onCloseBottomSheet}
        tools={tools}
        view={view}
        map={map}
        accountMap={accountMap}
        globalGroup={ globalGroup}
        currentAccountId={currentAccountId}
        basemapLayerRef={basemapLayerRef}
        editGeofenceGraphic={editGeofenceGraphic}
        setEditGeofenceGraphic={setEditGeofenceGraphic} />
    </>
  );
};

export default MoreMenuTool;
